import { Component, inject, OnInit, DestroyRef } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalModule, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HeaderComponent } from './components/header/header.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { DesignSystemModule } from '@compeople-shared/design-system-angular';
import { UserStore } from '@stores/user.store';
import { ErrorService } from '@services/error.service';
import { provideTranslocoScope, TranslocoPipe } from '@jsverse/transloco';
import { ModalSwitchBoxComponent } from './components/modal-switch-box/modal-switch-box.component';
import { SettingsMenuComponent } from './components/settings-menu/settings-menu.component';
import { computed, signal } from '@angular/core';

@Component({
  selector: 'art-root',
  imports: [
    RouterOutlet,
    MsalModule,
    DesignSystemModule,
    TranslocoPipe,
    HeaderComponent,
    NavigationComponent,
    ModalSwitchBoxComponent,
    SettingsMenuComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',

  providers: [provideTranslocoScope('error'), provideTranslocoScope('app')],
})
export class AppComponent implements OnInit {
  title = 'art-frontend';

  private destroyRef = inject(DestroyRef);

  protected errorService = inject(ErrorService);
  private authService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  protected userStore = inject(UserStore);
  private router = inject(Router);

  currentUrl = signal<string>('');

  readonly isSettingsRoute = computed(() => this.currentUrl().startsWith('/settings'));

  ngOnInit(): void {
    this.handleLoginRedirect();
    this.subscribeToLoginSuccess();
    this.trackRouteChanges();
  }

  private handleLoginRedirect() {
    this.authService
      .handleRedirectObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result == null) {
            const accounts = this.authService.instance.getAllAccounts();
            if (accounts.length > 0) {
              const account = accounts[0];
              this.authService.instance.setActiveAccount(account);
              this.userStore.fetchUserInformation();
            }
          }
        },
        error: (error) => console.error(error),
      });
  }

  private subscribeToLoginSuccess() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: EventMessage) => {
          const payload = result.payload as AuthenticationResult;
          this.authService.instance.setActiveAccount(payload.account);
          this.userStore.fetchUserInformation();
        },
        error: (error) => console.error(error),
      });
  }

  private trackRouteChanges() {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects);
      }
    });
  }

  onCloseErrorAlert() {
    this.errorService.clearError();
  }
}
